import { useEffect, useState } from 'react';
import type {
  RootCauseMethod,
  SafetyIncidentPayload,
  SafetyIncidentSeverity,
  SafetyIncidentType,
} from './types';
import {
  INCIDENT_TYPE_LABEL,
  ROOT_CAUSE_METHOD_LABEL,
  SEVERITY_LABEL,
} from './incidentsUtils';

const TYPES = Object.keys(INCIDENT_TYPE_LABEL) as SafetyIncidentType[];
const SEVERITIES = Object.keys(SEVERITY_LABEL) as SafetyIncidentSeverity[];
const METHODS = Object.keys(ROOT_CAUSE_METHOD_LABEL) as RootCauseMethod[];

interface IncidentFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: SafetyIncidentPayload) => Promise<void>;
  initial?: Partial<SafetyIncidentPayload>;
  title: string;
  showInvestigation?: boolean;
}

function toLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultPayload(): SafetyIncidentPayload {
  const now = new Date();
  const iso = now.toISOString();
  return {
    occurredAt: iso,
    reportedAt: iso,
    type: 'incidente',
    severity: 'leve',
    location: '',
    description: '',
    lostDays: 0,
  };
}

export function IncidentFormModal({
  open,
  onClose,
  onSubmit,
  initial,
  title,
  showInvestigation = false,
}: IncidentFormModalProps) {
  const [form, setForm] = useState<SafetyIncidentPayload>(defaultPayload());
  const [rootCauseText, setRootCauseText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...defaultPayload(), ...initial });
    const analysis = initial?.rootCauseAnalysis;
    if (analysis && typeof analysis === 'object' && 'summary' in analysis) {
      setRootCauseText(String((analysis as { summary?: string }).summary ?? ''));
    } else {
      setRootCauseText('');
    }
    setError(null);
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload: SafetyIncidentPayload = { ...form };
      if (showInvestigation && form.rootCauseMethod) {
        payload.rootCauseAnalysis = { summary: rootCauseText.trim().slice(0, 10_000) };
      }
      await onSubmit(payload);
      onClose();
    } catch {
      setError('No se pudo guardar el incidente.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4 sm:p-6">
      <div className="flex min-h-full items-start justify-center">
        <form
          className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 space-y-4 my-2 sm:my-4 shrink-0"
          onSubmit={handleSubmit}
        >
        <h2 className="text-lg font-semibold">{title}</h2>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-sm space-y-1">
            <span>Ocurrió</span>
            <input
              type="datetime-local"
              className="input w-full"
              required
              value={toLocalInput(form.occurredAt)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  occurredAt: new Date(e.target.value).toISOString(),
                }))
              }
            />
          </label>
          <label className="block text-sm space-y-1">
            <span>Reportado</span>
            <input
              type="datetime-local"
              className="input w-full"
              required
              value={toLocalInput(form.reportedAt)}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  reportedAt: new Date(e.target.value).toISOString(),
                }))
              }
            />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="block text-sm space-y-1">
            <span>Tipo</span>
            <select
              className="input w-full"
              value={form.type}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, type: e.target.value as SafetyIncidentType }))
              }
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {INCIDENT_TYPE_LABEL[type]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm space-y-1">
            <span>Severidad</span>
            <select
              className="input w-full"
              value={form.severity}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  severity: e.target.value as SafetyIncidentSeverity,
                }))
              }
            >
              {SEVERITIES.map((sev) => (
                <option key={sev} value={sev}>
                  {SEVERITY_LABEL[sev]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm space-y-1">
            <span>Días perdidos</span>
            <input
              type="number"
              min={0}
              className="input w-full"
              value={form.lostDays ?? 0}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, lostDays: Number(e.target.value) || 0 }))
              }
              placeholder="0"
            />
          </label>
        </div>

        <label className="block text-sm space-y-1">
          <span>Ubicación</span>
          <input
            className="input w-full"
            required
            value={form.location}
            onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
            placeholder="Ej: Planta 1 — sector logística"
          />
        </label>

        <label className="block text-sm space-y-1">
          <span>Persona afectada</span>
          <input
            className="input w-full"
            value={form.affectedPersonName ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, affectedPersonName: e.target.value || null }))
            }
            placeholder="Nombre de la persona afectada (opcional)"
          />
        </label>

        <label className="block text-sm space-y-1">
          <span>Descripción del hecho</span>
          <textarea
            className="input w-full min-h-[96px]"
            required
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Describa qué ocurrió, cómo y en qué circunstancias…"
          />
        </label>

        <label className="block text-sm space-y-1">
          <span>Acciones inmediatas</span>
          <textarea
            className="input w-full min-h-[72px]"
            value={form.immediateActions ?? ''}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, immediateActions: e.target.value || null }))
            }
            placeholder="Primeros auxilios, aislamiento del área, aviso a supervisión…"
          />
        </label>

        {showInvestigation && (
          <>
            <label className="block text-sm space-y-1">
              <span>Método causa raíz</span>
              <select
                className="input w-full"
                value={form.rootCauseMethod ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    rootCauseMethod: (e.target.value || null) as RootCauseMethod | null,
                  }))
                }
              >
                <option value="">—</option>
                {METHODS.map((method) => (
                  <option key={method} value={method}>
                    {ROOT_CAUSE_METHOD_LABEL[method]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm space-y-1">
              <span>Análisis de causa raíz</span>
              <textarea
                className="input w-full min-h-[96px]"
                value={rootCauseText}
                onChange={(e) => setRootCauseText(e.target.value)}
                placeholder="Conclusiones del análisis de causa raíz…"
              />
            </label>
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
}
